import * as React from 'react';
import { I18n } from 'react-redux-i18n';

import ActionsButton, { TRIGGER_TYPE } from '@common/components/ActionsButton';
import Tree, { TreeActionType, isRootItem, itemHasChildren, getRootItem, getItem } from '@common/components/Tree';
import { Button } from '@common/components/Button';
import { formatDate } from '@common/utils/date';
import { Flex } from '@common/components/Flex';
import { Header } from '@common/components/Header';
import { Label } from '@common/components/Label';
import { Table, TABLE_FIELD_FILTER_TYPE, TABLE_COLUMN_PREDEFINED_ACTION_TYPE } from '@common/components/Table';
import { MessageBox } from '@common/components/MessageBox';
import { EditableInput } from '@common/components/EditableInput';
import { deleteLetter, getLetters, openLetter, openLetterCreation, getFilters, renameLetter, moveLetter } from './actions/letters';
import { getFolders, deleteFolder, renameFolder, createFolder, moveFolder } from './actions/folders';
import { getAppConfig } from '@common/utils/config';
import { shallowIncludes } from '@common/utils/array';

import './Letters.styles.scss';

const loadItemsCount = 40;

export class Letters extends React.Component {
    appConfig = getAppConfig();

    state = {
        messageProps: {
            header: I18n.t('Delete Letter'),
            text: '',
            show: false,
            confirmText: I18n.t('Delete'),
            cancelText: I18n.t('Cancel'),
            size: 'medium',
            hideMessageBox: () => this.setMessageProps({ show: false }),
            onCustomOk: () => {},
        },
        collapsedFolders: [],
        selectedFolderId: null,
        selectedLetterId: null,
        editedLettersData: {},
        loadMetadata: {
            pagination: {}, 
            sorting: {},
            filtering: {},
        },
        searchKeyword: '',
        loading: {
            creatingLetter: false,
        },
        folders: [],
        letters: [],
        lettersCount: 0,
        filters: {},
    }

    componentDidMount() {
        this.initialize();
    }

    initialize = async () => {
        getFilters().then((filters) => {
            this.setState((x) => ({
                ...x,
                filters
            }))
        });
        const folders = await this.getFolders();
        const defaultFolderId = this.getDefaultFolderId(folders);

        if (defaultFolderId !== undefined) {
            this.setSelectedFolder(defaultFolderId);
        }
    }

    getFolders = async () => {
        const folders = await getFolders()
        
        this.setState((x) => ({
            ...x,
            folders
        }));

        return folders;
    }

    getDefaultFolderId(folders = this.state.folders) {
        const firstFolder = folders[0];

        return firstFolder?.id;
    }

    setMessageProps = (props) => {
        this.setState((state) => ({
            ...state,
            messageProps: {
                ...state.messageProps,
                ...props,
            },
        }));
    }

    closeMessage = () => {
        this.setMessageProps({ show: false });
    }

    resetLetters = () => {
        this.setState((x) => ({
            ...x,
            letters: [],
        }));
    }

    getLetters = (params) => {
        const requestParams = {
            ...this.state.loadMetadata,
            ...params,
        };
    
        getLetters({
            ...requestParams,
            filtering: {
                ...requestParams.filtering,
                searchKeyword: this.state.searchKeyword,
                folderId: this.state.selectedFolderId,
            },
        }).then((response) => {
			const shouldReset = requestParams.pagination.start === 0;
    
			if (shouldReset) {
				this.resetLetters();
			}

			const normalizedList = response.data.map((x) => ({
				...x,
				lastModified: formatDate(x.lastModified, { format: 'DD/MM/YYYY' }),
				createdDate: formatDate(x.createdDate, { format: 'DD/MM/YYYY' }),
			}));

            this.setState((x) => ({
                ...x,
                letters: x.letters.concat(normalizedList),
                lettersCount: response.count,
            }));
		},);
    }

    handleSearch = ({ searchKeyword }) => {
        const hasSearchKeyword = Boolean(searchKeyword);

        this.setState(
            (state) => ({
                ...state,
                searchKeyword,
                selectedFolderId: hasSearchKeyword 
                    ? null
                    : this.getDefaultFolderId(),
            }), 
            () => this.setCollapsedFolders([])
        );

        const metadata = {
            pagination: {
                ...this.state.loadMetadata.pagination,
                start: 0,
                size: loadItemsCount,
            },
        };
    
        this.setLoadMetadata(
            metadata, 
            this.getLetters
        );
    }

    setLoadMetadata = (metadata = {}, cb) => {
        this.setState(
            (state) => ({
                ...state,
                loadMetadata: {
                    ...this.state.loadMetadata,
                    ...metadata,
                }
            }), 
            cb
        );
    }

    resetFiltersMetadata = (cb) => {
        this.setState((state) => { 
            return {
                ...state, 
                searchKeyword: '',
                loadMetadata: {
                    ...state.loadMetadata,
                    filtering: {},
                }
            }
        }, cb);
    }

    setSelectedFolder = (id, cb = () => {}) => {
        this.setState(
            (state) => ({
                ...state,
                selectedFolderId: id
            }), 
            () => {
                cb();

                const getFiltersCallback = () => {
                    this.setLoadMetadata(
                        {
                            pagination: { 
                                ...this.state.loadMetadata.pagination,
                                start: 0, 
                                size: loadItemsCount 
                            } 
                        }, 
                        this.getLetters
                    );
                };

                this.resetFiltersMetadata(getFiltersCallback);
            }
        );
    }

    setCollapsedFolders = (folders) => {
        this.setState(
            (state) => ({
                ...state,
                collapsedFolders: folders
            }),
        );
    }

    handleOpenLetter = (id) => {
        openLetter(id);
    }

    handleDeleteLetter = (id) => {
        this.setMessageProps({
            show: true,
            text: I18n.t('Are you sure you want to delete the selected letter?'),
            onCustomOk: () => deleteLetter(id)
                .then(this.getLetters)
                .then(this.closeMessage)
        });
    }

    handleAddNewLetter = () => {
        this.setState((x) => ({ loading: { ...x.loading, creatingLetter: true } }));

        openLetterCreation(this.state.selectedFolderId)
            .finally(() => {
                this.setState((x) => ({ loading: { ...x.loading, creatingLetter: false } }));
            });
    }

    handleFolderChange = (id) => {
        this.setSelectedFolder(id);
    }

    handleRenameLetter = (id) => {
        this.setState((state) => ({ ...state, selectedLetterId: id }));
    }

    handleSaveLetterName = (name) => {
        const { editedLettersData, selectedLetterId } = this.state;
        const previousSavedData = editedLettersData[selectedLetterId] || {};

        this.setState((state) => ({ 
            ...state,
            editedLettersData: {
                ...editedLettersData,
                [selectedLetterId]: {
                    ...previousSavedData,
                    name,
                }
            },
        }));

        renameLetter({ letterId: selectedLetterId, name });
        
        this.setState((state) => ({ ...state, selectedLetterId: null }));
    }

    renderEditingName = (name) => {
        return (
            <EditableInput
                classes={{
                    root: 'Letters__content__editing-name',
                    input: 'Letters__content__editing-name__input',
                }} 
                defaultValue={name}
                onSave={this.handleSaveLetterName}
            />
        );
    }

    handleMoveLetter = (letterId) => async (folderId) => {
        await moveLetter({ letterId, folderId });
        this.setLoadMetadata(
            {
                pagination: {
                    ...this.state.loadMetadata.pagination,
                    start: 0,
                }
            }, 
            this.getLetters
        );
    }

    getActions = (letter) => {
        const { folders } = this.state;

        return [
            { label: I18n.t('Edit'), onClick: this.handleOpenLetter  },
            { 
                label: (
                    <ActionsButton
                        withBackdrop={false}
                        id={`folders--letter-id-${letter.id}`}
                        triggerType={TRIGGER_TYPE.hover}
                        trigger={<React.Fragment>{I18n.t('Move')} <span className="fa fa-caret-right" /></React.Fragment>}
                        className="Letters__content__move-to-folder-actions-btn"
                    >
                        <b className="Letters__content__move-to-folder-actions-btn__label">{I18n.t('Move Into')}</b>
                        <Tree 
                            features={{
                                collapsing: false,
                            }}
                            shouldDisableItem={(item) => {
                                const currentMovingItem = getItem(letter.folderId, folders);
                                const shouldDisable = (
                                    shallowIncludes([letter.folderId], item.id) ||
                                    this.shouldDisableItemOnMove({
                                        currentMovingItem,
                                        regularItem: item,
                                        items: folders,
                                    })
                                );
                                
                                return shouldDisable;
                            }}
                            items={folders}
                            onChange={this.handleMoveLetter(letter.id)} 
                        />
                    </ActionsButton>
                ),  
            },
            { label: I18n.t('Rename'), onClick: this.handleRenameLetter },
            { label: I18n.t('Delete'), onClick: this.handleDeleteLetter },
        ];
    }

    renderName = ({ original }) => {
        const { editedLettersData } = this.state;
        const letterData = editedLettersData[original.id]; 
        const name = letterData ? letterData.name : original.name;

        return original.id == this.state.selectedLetterId
            ? this.renderEditingName(name)
            : (
                <Button
                    variant="text"
                    text={name}
                    onClick={() => this.handleOpenLetter(original.id, original)}
                />
            )
    }

    get columns() {
        return [
            { 
                disableActions: true,
                resizable: false,
                sortable: false,
                Header: 'Actions', 
                Cell: ({ original }) => (
                    <ActionsButton 
                        id={original.id} 
                        data={original} 
                        menuItems={this.getActions(original)}
                    />
                ),
                minWidth: 65,
                className: 'table-actions-cell'
            },
            { 
                Header: 'Name', 
                accessor: 'name', 
                minWidth: 200, 
                Cell: this.renderName
            },
            { Header: 'Last Modified', accessor: 'lastModified', minWidth: 200, filterProps: { type: TABLE_FIELD_FILTER_TYPE.date }, },
            { Header: 'Modified By', accessor: 'lastModifiedBy', minWidth: 200 },
            { Header: 'Created Date', accessor: 'createdDate', minWidth: 200, filterProps: { type: TABLE_FIELD_FILTER_TYPE.date }, },
            { Header: 'Created By', accessor: 'createdBy', minWidth: 200 },
        ].map(({ sortable = true, Header, minWidth, ...otherProps }) => {
            return {
                sortable,
                minWidth,
                Header: I18n.t(Header),
                width: minWidth,
                ...otherProps,
            };
        });
    }

    handleFolderCreateRetry = async (id, name) => {
        await renameFolder(id, name);
        await this.getFolders();
    }

    handleFolderRename = async (id, name) => {
        if(name.trim() === '') {
            this.setMessageProps({
                header: I18n.t('Operation canceled'),
                content: I18n.t('Folder name cannot be empty'),
                show: true,
                confirmText: 'Ok',
                onCustomOk: this.closeMessage,
            });
            return;
        }
        await renameFolder(id, name);
        await this.getFolders();
    }

    handleFolderDelete = async (folderId) => {
        await deleteFolder(folderId);
        await this.getFolders();
    }

    handleFolderCreate = async (parentFolderId, name) => {
        const createdFolder = await createFolder(parentFolderId);

        try {
            await renameFolder(createdFolder.id, name);
            await this.getFolders();

            return {
                item: createdFolder,
            };
        } catch (err) {
            return {
                item: createdFolder,
                error: err,
            };
        }
    }

    handleFolderMove = async (folderId, toFolderId) => {
        await moveFolder(folderId, toFolderId);
        await this.getFolders();
    }

    getFoldersActions = (item, items) => {
        return [
            {   type: TreeActionType.create   },
            {
                type: TreeActionType.move,
                condition: !isRootItem(item, items),
            },
            {
                type: TreeActionType.rename,
                condition: !isRootItem(item, items),
            },
            {
                type: TreeActionType.delete,
                condition: !isRootItem(item, items) && !itemHasChildren(item),
            },
        ]
        .filter(({ condition = true }) => condition)
        .map(({ type }) => ({ type }));
    }

    shouldDisableItemOnMove = ({ currentMovingItem, regularItem, items }) => {
        const checks = [
            () => {
                return isRootItem(regularItem, items);
            },
            () => {
                const rootOfMovingItem = getRootItem(currentMovingItem.id, items);
                const rootOFRegularItem = getRootItem(regularItem.id, items);

                return rootOfMovingItem.id !== rootOFRegularItem.id;
            }
        ];

        return checks.some((compute) => compute());
    }

    render() {
        const { letters, folders, filters, lettersCount } = this.state;
        const lettersConfig = this.appConfig.letters;
        const { collapsedFolders } = this.state;

        return (
            <div className="Letters">
                <MessageBox
                    {...this.state.messageProps}
                />
                <Header 
                    moduleName={I18n.t("Letter Templates")}
                    className="Letters__header"
                    withLiveSearch={lettersConfig.liveSearchEnabled}
                    liveSearchDelay={lettersConfig.liveSearchDelay}
                    searchKeyword={this.state.searchKeyword}
                    onSearch={this.handleSearch}
                >
                    <button disabled={this.state.loading.creatingLetter} className="btn btn-primary" onClick={this.handleAddNewLetter}>
                        <i className="fa fa-plus"></i>
                        {I18n.t('New Template')}
                    </button>
                </Header>
                <Flex width="100%" wrap="nowrap" className="Letters__content">
                    <div className="Letters__content__folder">
                        <Label className="Letters__content__folder__label" text={I18n.t("Folder")} />
                        <Tree
                            className="Letters__content__folder__inner"
                            labels={{
                                deleteItem: I18n.t('Are you sure you want to delete a folder?')
                            }}
                            items={folders}
                            value={this.state.selectedFolderId} 
                            collapsedItems={collapsedFolders}
                            onCollapsedItemsChange={this.setCollapsedFolders}
                            shouldDisableItemOnMove={this.shouldDisableItemOnMove}
                            getActions={this.getFoldersActions}
                            onRename={this.handleFolderRename}
                            onDelete={this.handleFolderDelete}
                            onCreate={this.handleFolderCreate}
                            onCreateRetry={this.handleFolderCreateRetry}
                            onCreateCancel={this.handleFolderDelete}
                            onMove={this.handleFolderMove}
                            onChange={this.handleFolderChange} 
                        />
                    </div>
                    <div className="Letters__content__table">
                        <Table
                            infiniteScroll
                            disableAutoInit
                            columnActions={[{ type: TABLE_COLUMN_PREDEFINED_ACTION_TYPE.filters }]}
                            moduleName="Letters"
                            filters={filters}
                            noDataMessage={I18n.t('There no letters to display')}
                            config={{
                                data: letters,
                                columns: this.columns,
                            }}
                            loadMetadata={{
                                filtering: this.state.loadMetadata.filtering
                            }}
                            loadItemsCount={loadItemsCount}
                            itemsCount={lettersCount}
                            onLoadData={this.getLetters}
                            setLoadMetadata={this.setLoadMetadata}
                        />
                    </div>
                </Flex>
            </div>
        );
    }
}
